// ─────────────────────────────────────────────────────────────
// AdminPage — served at /admin via React Router.
// Password-gated. Three tabs: Users, Password & Data, System Library.
// Uses the same theme as the rest of the app.
// ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ThemeProvider,
  CssBaseline,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { MapsLogo } from "../../components/MapsLogo";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { SessionsTab } from "./SessionsTab";
import Editor from "@monaco-editor/react";
import { useColorMode } from "../../hooks/useColorMode";
import { createDashboardTheme } from "../../theme";
import {
  adminSystemImagesApi,
  adminSystemScriptsApi,
  adminUsersApi,
  imageApi,
  maintenanceApi,
  roleQuotasApi,
  domainRestrictionApi,
} from "../../api/client";
import type {
  AdminUser,
  AvailableMarieModel,
  MaintenanceStatus,
  RoleQuotaSettings,
  DomainRestrictionConfig,
  UserImage,
  UserRole,
  LibraryScript,
} from "../../api/client";

// ── Tier role helpers ─────────────────────────────────────────
//
// Sam (2026-05-14) — three-tier QoS roles. Display labels live on the
// frontend; ids must stay byte-for-byte equal to the backend enum.

const ROLE_OPTIONS: { value: UserRole; label: string; description: string }[] = [
  {
    value: "basic",
    label: "Basic",
    description: "Throttled (daily Marie cap, hourly script-run cap) and limited to the configured model subset.",
  },
  {
    value: "advanced",
    label: "Advanced",
    description: "Bypasses every basic limit and has full model access. No admin panel.",
  },
  {
    value: "admin",
    label: "Admin",
    description: "Advanced access + admin panel + system management.",
  },
];

function roleLabel(role: UserRole): string {
  return ROLE_OPTIONS.find((r) => r.value === role)?.label ?? role;
}

/**
 * The seed/system account is normally filtered out by `GET /api/admin/users`
 * already (backend excludes it from the listing), so this guard is
 * defensive. Sam's backend additionally rejects role-mutation writes
 * with `400 "Cannot modify the system account"` — we pre-disable the
 * dropdown for it client-side so an inconsistent state never lets the
 * dialog open against an unfixable target.
 */
function isSystemAccount(user: AdminUser): boolean {
  // System account email is hard-coded in backend/app.py
  // (`_ensure_system_account` — system@mapsscripthelper.internal).
  return user.email === "system@mapsscripthelper.internal";
}
import { formatRelativeTime } from "../../utils/formatRelativeTime";

// ── Users Tab ────────────────────────────────────────────────

function formatLastLogin(value: string | null): string {
  if (!value) return "Never";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function UsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Snackbar shown after a successful role change so the admin gets a
  // friendly "Promoted Jane to Advanced" confirmation matching the
  // Maintenance card snack tone. Sam dropped the X-Admin-Password gate on
  // the per-user role endpoint in his 2026-05-14 follow-up — the dropdown
  // now writes directly on change, no confirmation dialog.
  const [snack, setSnack] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    adminUsersApi
      .list()
      .then(setUsers)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load users"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.full_name ?? "").toLowerCase().includes(q) ||
        (u.job_title ?? "").toLowerCase().includes(q)
    );
  }, [users, search]);

  /**
   * Apply a role change directly from the Select dropdown. Sam dropped
   * the X-Admin-Password gate on the per-user role endpoint
   * (2026-05-14 follow-up) — an admin JWT is sufficient, so we write
   * straight through. The backend still enforces guards (can't change
   * your own role, can't modify the system account); those come back
   * as friendly error messages which we surface inline via `actionError`.
   */
  async function handleRoleChange(user: AdminUser, nextRole: UserRole) {
    if (nextRole === user.role) return;
    setActionBusy(user.id);
    setActionError(null);
    try {
      const updated = await adminUsersApi.setRole(user.id, nextRole);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      const displayName = user.full_name ?? user.username ?? user.email;
      setSnack(`Set ${displayName} to ${roleLabel(updated.role)}.`);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : "Failed to update role",
      );
    } finally {
      setActionBusy(null);
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await adminUsersApi.remove(deleteTarget.id);
      setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      setDeleteTarget(null);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <TextField
        size="small"
        placeholder="Search by name or email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 320 }}
      />

      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error">{error}</Alert>
      )}

      {!loading && !error && (
        <Box
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 0.5,
            overflow: "auto",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "background.default" }}>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary" }}>
                  User
                </TableCell>
                {/* Job title from SSO (Sam, 2026-05-15). Read-only —
                    sourced from the TF IdP token, refreshed on login.
                    220px is wide enough to show "Senior Materials
                    Scientist" without truncation; longer titles fall
                    back to the default MUI text-overflow tooltip. */}
                <TableCell sx={{ fontWeight: 600, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", width: 220 }}>
                  Title
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", width: 160 }}>
                  Role
                </TableCell>
                {/* Per-user script counts (Sam, 2026-05-14 follow-up).
                    Numeric right-aligned; narrow widths since values are
                    always small integers. */}
                <TableCell sx={{ fontWeight: 600, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", width: 70 }} align="right">
                  Saved
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", width: 80 }} align="right">
                  Published
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", width: 140 }}>
                  Last Login
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", width: 60 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.disabled">
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((user) => (
                  <TableRow key={user.id} hover>
                    {/* User column */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {user.full_name ?? user.username ?? user.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                        {user.provider !== "local" && (
                          <Chip
                            label={user.provider}
                            size="small"
                            sx={{ height: 16, fontSize: "0.5625rem" }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* Title column (Sam, 2026-05-15). Em-dash + dim
                        secondary when null (no IdP-supplied title) to
                        match the visual treatment of other "no data"
                        cells. Default browser tooltip on the Typography
                        text shows the full value when the column
                        truncates. */}
                    <TableCell>
                      <Typography
                        variant="caption"
                        color={user.job_title ? "text.primary" : "text.disabled"}
                        title={user.job_title ?? undefined}
                        sx={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.job_title ?? "—"}
                      </Typography>
                    </TableCell>

                    {/* Role column — dropdown.
                        Replaces the legacy admin-only IconButton (Sam,
                        2026-05-14). Admin promotion still happens here
                        — selecting "Admin" sets role='admin' and the
                        backend keeps `is_admin` in sync. The Select is
                        disabled for the system account (defensive — the
                        list endpoint already excludes it). */}
                    <TableCell>
                      {isSystemAccount(user) ? (
                        <Chip
                          label={roleLabel(user.role)}
                          size="small"
                          color="default"
                          sx={{ height: 18, fontSize: "0.625rem" }}
                        />
                      ) : (
                        <Select
                          size="small"
                          value={user.role}
                          onChange={(e) => {
                            handleRoleChange(user, e.target.value as UserRole);
                          }}
                          disabled={actionBusy === user.id}
                          renderValue={(v) => (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              {v === "admin" && (
                                <AdminPanelSettingsOutlinedIcon
                                  sx={{ fontSize: 14, color: "primary.main" }}
                                />
                              )}
                              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                                {roleLabel(v as UserRole)}
                              </Typography>
                              {actionBusy === user.id && (
                                <CircularProgress size={12} />
                              )}
                            </Box>
                          )}
                          sx={{
                            fontSize: "0.75rem",
                            height: 28,
                            minWidth: 130,
                            "& .MuiSelect-select": { py: "3px", px: "8px" },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
                          }}
                        >
                          {ROLE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <Box>
                                <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
                                  {opt.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: "0.6875rem" }}
                                >
                                  {opt.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>

                    {/* Saved / Published script counts (Sam,
                        2026-05-14 follow-up). Right-aligned monospace-y
                        numerics; dim 0-values so non-creators don't
                        compete visually with the actual contributors. */}
                    <TableCell align="right">
                      <Typography
                        variant="caption"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          color:
                            user.saved_scripts_count > 0
                              ? "text.primary"
                              : "text.disabled",
                        }}
                      >
                        {user.saved_scripts_count}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="caption"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          color:
                            user.published_scripts_count > 0
                              ? "text.primary"
                              : "text.disabled",
                        }}
                      >
                        {user.published_scripts_count}
                      </Typography>
                    </TableCell>

                    {/* Last login column */}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatLastLogin(user.last_login)}
                      </Typography>
                    </TableCell>

                    {/* Actions column — delete only. Role changes moved
                        into the Role column dropdown (Sam, 2026-05-14). */}
                    <TableCell align="right">
                      <Tooltip title="Delete user">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(user)}
                          aria-label="Delete user"
                          sx={{ minWidth: 32, minHeight: 32 }}
                        >
                          <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete User?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete "{deleteTarget?.full_name ?? deleteTarget?.email}"? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteBusy}
            onClick={handleDeleteConfirm}
            startIcon={deleteBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role change is JWT-only — no confirmation dialog. The dropdown
          writes through immediately via handleRoleChange, and errors
          surface inline in the `actionError` Alert above. As of Greg's
          2026-05-15 policy, the Maintenance and Basic User Limits
          cards are also JWT-only — every save in the admin panel now
          relies on the admin JWT. The `AdminPasswordPrompt` component
          is retained (currently unused) for future Danger Zone wiring,
          where a heightened check is still required. */}

      <Snackbar
        open={snack !== null}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ width: "100%" }}
        >
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ── Maintenance Mode Card ─────────────────────────────────────
//
// Lives at the top of the Password & Data tab. Two-step flow:
//   • Toggle Switch + (when enabling) a 500-char message field.
//   • Save button fires the API call directly — no password prompt.
//
// Greg's policy (2026-05-15): "Saving anything in the admin panel should
// not require an admin password again — admins can only get into this
// area." The admin JWT is sufficient; Sam's `verify_admin` dependency
// now accepts JWT OR password header, so the password dialog has been
// removed. Danger Zone retains a heightened check (see PasswordDataTab).

interface AdminPasswordPromptProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  confirmColor: "warning" | "success";
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
}

function AdminPasswordPrompt({
  open,
  title,
  description,
  confirmLabel,
  confirmColor,
  onClose,
  onSubmit,
}: AdminPasswordPromptProps) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setErrorMsg(null);
      setBusy(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (!password.trim()) {
      setErrorMsg("Password is required");
      return;
    }
    setBusy(true);
    setErrorMsg(null);
    try {
      await onSubmit(password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      const lower = msg.toLowerCase();
      if (
        msg.includes("403") ||
        lower.includes("forbidden") ||
        lower.includes("incorrect") ||
        lower.includes("invalid")
      ) {
        setErrorMsg("Incorrect password");
      } else {
        setErrorMsg(msg);
      }
      setBusy(false);
    }
  }, [password, onSubmit]);

  const colorSx =
    confirmColor === "warning"
      ? {
          backgroundColor: "warning.main",
          color: "#fff",
          "&:hover": { backgroundColor: "warning.dark" },
        }
      : {
          backgroundColor: "success.main",
          color: "#fff",
          "&:hover": { backgroundColor: "success.dark" },
        };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
    >
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        <TextField
          inputRef={inputRef}
          label="Admin Password"
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (errorMsg) setErrorMsg(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !busy) void handleSubmit();
          }}
          fullWidth
          autoFocus
          disabled={busy}
          error={Boolean(errorMsg)}
          helperText={errorMsg ?? " "}
          autoComplete="current-password"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={busy || !password.trim()}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={colorSx}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const MAINTENANCE_MESSAGE_LIMIT = 500;

function MaintenanceModeCard() {
  const [status, setStatus] = useState<MaintenanceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Pending toggle state — what the admin has staged but not yet saved.
  // We mirror status.enabled into pendingEnabled on load, then let the
  // admin freely toggle it without firing API calls.
  const [pendingEnabled, setPendingEnabled] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");

  // In-flight + snackbar state. Greg's 2026-05-15 policy removed the
  // admin-password dialog from this card — the Save button writes
  // straight through using the JWT.
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      const next = await maintenanceApi.status();
      setStatus(next);
      setPendingEnabled(next.enabled);
      setPendingMessage(next.message ?? "");
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isOn = status?.enabled === true;
  const messageLengthOver = pendingMessage.length > MAINTENANCE_MESSAGE_LIMIT;
  const isDirty =
    status !== null &&
    (pendingEnabled !== status.enabled ||
      // Message edits only matter when we're enabling — the backend ignores
      // message on disable. But surface the dirty state if the user has
      // changed both fields together so they don't lose the message.
      (pendingEnabled && pendingMessage !== (status.message ?? "")));

  const handleSaveClick = async () => {
    if (messageLengthOver || saveBusy) return;
    setSaveBusy(true);
    setSaveError(null);
    try {
      const trimmed = pendingMessage.trim();
      const next = await maintenanceApi.set(
        pendingEnabled,
        pendingEnabled ? (trimmed ? trimmed : null) : null,
      );
      setStatus(next);
      setPendingEnabled(next.enabled);
      setPendingMessage(next.message ?? "");
      setSnack(
        next.enabled
          ? "Maintenance mode enabled — visitors will see the maintenance screen on their next request."
          : "Maintenance mode disabled — site is back up."
      );
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to update maintenance mode");
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <BuildOutlinedIcon color="warning" sx={{ fontSize: 20 }} />
          <Typography variant="h3">Maintenance Mode</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : loadError ? (
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={() => void refresh()}>Retry</Button>
          }>
            {loadError}
          </Alert>
        ) : (
          <Stack spacing={2}>
            {/* Status row */}
            <Box
              sx={{
                p: 1.5,
                bgcolor: "background.default",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontWeight: 600,
                }}>
                  Status
                </Typography>
                <Chip
                  label={isOn ? "ON" : "OFF"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: isOn ? "warning.main" : "success.main",
                    color: "#fff",
                    height: 22,
                    minWidth: 48,
                  }}
                />
              </Box>
              {isOn && status?.message && (
                <Typography variant="body2" color="text.primary" sx={{ fontStyle: "italic" }}>
                  {status.message}
                </Typography>
              )}
              {status?.updated_at && (
                <Typography variant="caption" color="text.secondary">
                  Last toggled: {formatRelativeTime(status.updated_at)}
                </Typography>
              )}
            </Box>

            {/* Toggle */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Switch
                checked={pendingEnabled}
                onChange={(e) => setPendingEnabled(e.target.checked)}
                color="warning"
                inputProps={{ "aria-label": "Toggle maintenance mode" }}
              />
              <Typography variant="body2">
                Maintenance Mode: <strong>{pendingEnabled ? "On" : "Off"}</strong>
              </Typography>
            </Box>

            {/* Message field — only meaningful when enabling */}
            {pendingEnabled && (
              <TextField
                label="Message to visitors (optional)"
                placeholder="e.g. We're updating around 3pm Pacific. Back shortly!"
                value={pendingMessage}
                onChange={(e) => setPendingMessage(e.target.value)}
                multiline
                minRows={2}
                maxRows={5}
                fullWidth
                size="small"
                error={messageLengthOver}
                helperText={`${pendingMessage.length} / ${MAINTENANCE_MESSAGE_LIMIT}${messageLengthOver ? " — message is too long" : ""}`}
              />
            )}

            {/* Inline save error — JWT path can still fail
                (network, 5xx, etc.) so we surface it next to the action
                rather than re-introducing a modal. */}
            {saveError && (
              <Alert severity="error" onClose={() => setSaveError(null)}>
                {saveError}
              </Alert>
            )}

            {/* Save button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color={pendingEnabled ? "warning" : "success"}
                disabled={!isDirty || messageLengthOver || saveBusy}
                onClick={handleSaveClick}
                startIcon={saveBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{
                  ...(pendingEnabled
                    ? {
                        bgcolor: "warning.main",
                        color: "#fff",
                        "&:hover": { bgcolor: "warning.dark" },
                      }
                    : {
                        bgcolor: "success.main",
                        color: "#fff",
                        "&:hover": { bgcolor: "success.dark" },
                      }),
                }}
              >
                {pendingEnabled ? "Enable Maintenance Mode" : "Disable Maintenance Mode"}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>

      <Snackbar
        open={snack !== null}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ width: "100%" }}
        >
          {snack}
        </Alert>
      </Snackbar>
    </Card>
  );
}

// ── Basic User Limits Card ────────────────────────────────────
//
// Sits beneath the Maintenance Mode card on the Password & Data tab.
// Runtime-configurable QoS knobs for the `basic` tier (Sam, 2026-05-14):
//   • Daily Marie chat cap
//   • Hourly script-run cap
//   • Allowed model subset (checkbox grid against the live model catalog)
//
// All three settings apply ONLY to users with role='basic'. Promoting to
// advanced or admin bypasses them — the backend gate short-circuits for
// non-basic users without ever consulting these values.
//
// PUT is JWT-admin (Sam's 2026-05-15 follow-up: `verify_admin` accepts
// JWT OR X-Admin-Password). Greg's policy: no password prompt for
// in-panel saves — the JWT is sufficient. Backend invalidates its
// in-process cache synchronously, so changes take effect on the next
// request.

interface BasicUserLimitsCardProps {
  /** Triggered when state changes for testing/parent observers. Optional. */
  onSaved?: (settings: RoleQuotaSettings) => void;
}

function BasicUserLimitsCard(_: BasicUserLimitsCardProps) {
  const [settings, setSettings] = useState<RoleQuotaSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Pending (staged) values. Mirrored from `settings` on load; admin
  // edits these without firing API writes until Save is pressed.
  const [chatsPerDay, setChatsPerDay] = useState<number>(20);
  const [runsPerHour, setRunsPerHour] = useState<number>(10);
  const [allowedModelIds, setAllowedModelIds] = useState<Set<string>>(new Set());

  // In-flight + snackbar state. Greg's 2026-05-15 policy removed the
  // admin-password dialog — Save writes straight through with the JWT.
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const next = await roleQuotasApi.get();
      setSettings(next);
      setChatsPerDay(next.basic_marie_chats_per_day);
      setRunsPerHour(next.basic_script_runs_per_hour);
      setAllowedModelIds(new Set(next.basic_allowed_models));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load tier quotas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Client-side validation matches the backend's Pydantic constraints
  // so the user gets immediate feedback instead of a network round-trip
  // 422. The backend is still the authority — these are UX nudges.
  const chatsInvalid = !Number.isInteger(chatsPerDay) || chatsPerDay < 1 || chatsPerDay > 100000;
  const runsInvalid = !Number.isInteger(runsPerHour) || runsPerHour < 1 || runsPerHour > 100000;
  const modelsInvalid = allowedModelIds.size === 0;
  const formInvalid = chatsInvalid || runsInvalid || modelsInvalid;

  const isDirty =
    settings !== null &&
    (chatsPerDay !== settings.basic_marie_chats_per_day ||
      runsPerHour !== settings.basic_script_runs_per_hour ||
      // Cheap order-insensitive set comparison.
      allowedModelIds.size !== settings.basic_allowed_models.length ||
      settings.basic_allowed_models.some((id) => !allowedModelIds.has(id)));

  function toggleModel(id: string) {
    setAllowedModelIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSaveClick() {
    if (formInvalid || !isDirty || saveBusy) return;
    setSaveBusy(true);
    setSaveError(null);
    try {
      const next = await roleQuotasApi.set({
        basic_marie_chats_per_day: chatsPerDay,
        basic_script_runs_per_hour: runsPerHour,
        basic_allowed_models: Array.from(allowedModelIds),
      });
      setSettings(next);
      setChatsPerDay(next.basic_marie_chats_per_day);
      setRunsPerHour(next.basic_script_runs_per_hour);
      setAllowedModelIds(new Set(next.basic_allowed_models));
      setSnack("Tier quotas saved — changes apply to basic users on their next request.");
      _.onSaved?.(next);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save tier quotas");
    } finally {
      setSaveBusy(false);
    }
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <TuneOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="h3">Basic User Limits</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Settings only apply to users with the <strong>basic</strong> role.
          Advanced and admin tiers bypass these caps and have full model access.
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : loadError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void refresh()}>
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        ) : settings ? (
          <Stack spacing={2}>
            {/* Numeric caps */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Marie chats per day"
                type="number"
                value={chatsPerDay}
                onChange={(e) => setChatsPerDay(parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 1, max: 100000, step: 1 }}
                error={chatsInvalid}
                helperText={chatsInvalid ? "Must be a positive integer (≤ 100,000)." : " "}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                size="small"
                label="Script runs per hour"
                type="number"
                value={runsPerHour}
                onChange={(e) => setRunsPerHour(parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 1, max: 100000, step: 1 }}
                error={runsInvalid}
                helperText={runsInvalid ? "Must be a positive integer (≤ 100,000)." : " "}
                sx={{ flex: 1, minWidth: 200 }}
              />
            </Box>

            {/* Model allowlist */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Models allowed for basic users
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Pick at least one model. Models not checked here will return 403 for
                basic users; advanced and admin tiers can still pick any model.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: 0.5,
                  p: 1,
                  borderRadius: 0.5,
                  bgcolor: "background.default",
                  border: "1px solid",
                  borderColor: modelsInvalid ? "error.main" : "divider",
                }}
              >
                {settings.available_models.map((m: AvailableMarieModel) => (
                  <FormControlLabel
                    key={m.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={allowedModelIds.has(m.id)}
                        onChange={() => toggleModel(m.id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: "0.8125rem", lineHeight: 1.2 }}>
                          {m.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6875rem" }}>
                          {m.provider} · {m.id}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0.25 } }}
                  />
                ))}
              </Box>
              {modelsInvalid && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  Select at least one model.
                </Typography>
              )}
            </Box>

            {/* Inline save error — surfaced next to the action rather
                than via a modal, matching the maintenance card pattern. */}
            {saveError && (
              <Alert severity="error" onClose={() => setSaveError(null)}>
                {saveError}
              </Alert>
            )}

            {/* Audit footer + Save */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="caption" color="text.secondary">
                {settings.updated_at
                  ? `Last updated ${formatRelativeTime(settings.updated_at)}${settings.updated_by ? ` by ${settings.updated_by}` : ""}.`
                  : "No edits yet."}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                disabled={!isDirty || formInvalid || saveBusy}
                onClick={handleSaveClick}
                startIcon={saveBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                Save Tier Quotas
              </Button>
            </Box>
          </Stack>
        ) : null}
      </CardContent>

      <Snackbar
        open={snack !== null}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ width: "100%" }}
        >
          {snack}
        </Alert>
      </Snackbar>
    </Card>
  );
}

// ── Domain Restriction Card ───────────────────────────────────
//
// Sits beneath the Basic User Limits card on the Password & Data tab.
// Beta access control (Sam, 2026-05-20):
//   • Master toggle — "Restrict logins to approved domains". When ON,
//     register/login/SSO are blocked for anyone whose email domain isn't
//     in the list. Admins are always exempt.
//   • Managed domain list — chips with a remove control + an input/Add
//     button to append a new domain.
//
// Unlike the Maintenance / Basic-Limits cards (stage-then-Save), every
// mutation here writes through immediately and refreshes from the full
// `{enabled, domains}` config the backend returns — there's no "Save"
// step to forget, and concurrent admins never lose each other's edits.
// JWT-admin only (Greg's 2026-05-15 policy — no password prompt for
// in-panel saves; `verify_admin` accepts the admin JWT).

// Light client-side check so obviously-bad input gets immediate feedback.
// The backend is the authority (it lowercases + trims and re-validates);
// this only spares the user a round-trip on clearly malformed entries.
function isLikelyValidDomain(raw: string): boolean {
  const d = raw.trim().toLowerCase();
  if (!d) return false;
  if (d.includes("@")) return false;
  if (/\s/.test(d)) return false;
  if (!d.includes(".")) return false;
  return true;
}

function DomainRestrictionCard() {
  const [config, setConfig] = useState<DomainRestrictionConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Toggle write-in-flight (separate from per-domain ops so the Switch
  // can show its own busy state without freezing the list).
  const [toggleBusy, setToggleBusy] = useState(false);
  // Per-domain remove in-flight — keyed by domain so each chip can show
  // its own spinner / disabled state independently.
  const [removingDomain, setRemovingDomain] = useState<string | null>(null);

  // Add-domain field.
  const [newDomain, setNewDomain] = useState("");
  const [addBusy, setAddBusy] = useState(false);

  // Mutation error (toggle / add / remove) surfaced inline.
  const [actionError, setActionError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const next = await domainRestrictionApi.get();
      setConfig(next);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load domain restriction settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isOn = config?.enabled === true;

  async function handleToggle(nextEnabled: boolean) {
    if (toggleBusy) return;
    setToggleBusy(true);
    setActionError(null);
    try {
      const next = await domainRestrictionApi.setEnabled(nextEnabled);
      setConfig(next);
      setSnack(
        next.enabled
          ? "Domain restriction enabled — only approved domains can register or log in (admins always exempt)."
          : "Domain restriction disabled — anyone can register and log in."
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to update the toggle");
    } finally {
      setToggleBusy(false);
    }
  }

  async function handleAddDomain() {
    const candidate = newDomain.trim().toLowerCase();
    if (!candidate || addBusy) return;
    setAddBusy(true);
    setActionError(null);
    try {
      const next = await domainRestrictionApi.addDomain(candidate);
      setConfig(next);
      setNewDomain("");
      setSnack(`Added "${candidate}" to the approved-domain list.`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to add domain");
    } finally {
      setAddBusy(false);
    }
  }

  async function handleRemoveDomain(domain: string) {
    if (removingDomain) return;
    setRemovingDomain(domain);
    setActionError(null);
    try {
      const next = await domainRestrictionApi.removeDomain(domain);
      setConfig(next);
      setSnack(`Removed "${domain}" from the approved-domain list.`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to remove domain");
    } finally {
      setRemovingDomain(null);
    }
  }

  // Light client feedback: the field is only "addable" when it looks like
  // a domain and isn't already present (case-insensitive). The backend
  // dedupes anyway, but we'd rather not fire a no-op.
  const trimmedNew = newDomain.trim().toLowerCase();
  const alreadyPresent =
    trimmedNew !== "" && (config?.domains.some((d) => d.toLowerCase() === trimmedNew) ?? false);
  const newDomainLooksValid = isLikelyValidDomain(newDomain);
  const canAdd = newDomainLooksValid && !alreadyPresent && !addBusy;
  const addHelper =
    newDomain.trim() === ""
      ? "e.g. thermofisher.com"
      : alreadyPresent
        ? "Already on the list."
        : !newDomainLooksValid
          ? "Enter a bare domain (must contain a dot, no “@” or spaces)."
          : " ";

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <VerifiedUserOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="h3">Domain Restriction</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When <strong>on</strong>, only people whose email domain is on the approved
          list can register or log in (including SSO). Admins are <strong>always
          exempt</strong>. Turn this <strong>off</strong> to reopen the app to anyone —
          e.g. at public launch.
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : loadError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void refresh()}>
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        ) : config ? (
          <Stack spacing={2}>
            {/* Status + master toggle */}
            <Box
              sx={{
                p: 1.5,
                bgcolor: "background.default",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}
                >
                  Status
                </Typography>
                <Chip
                  label={isOn ? "RESTRICTED" : "OPEN"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: isOn ? "warning.main" : "success.main",
                    color: "#fff",
                    height: 22,
                    minWidth: 48,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Switch
                  checked={isOn}
                  onChange={(e) => void handleToggle(e.target.checked)}
                  disabled={toggleBusy}
                  color="warning"
                  inputProps={{ "aria-label": "Restrict logins to approved domains" }}
                />
                <Typography variant="body2">
                  Restrict logins to approved domains:{" "}
                  <strong>{isOn ? "On" : "Off"}</strong>
                </Typography>
                {toggleBusy && <CircularProgress size={14} />}
              </Box>
            </Box>

            {/* Mutation error (toggle / add / remove) */}
            {actionError && (
              <Alert severity="error" onClose={() => setActionError(null)}>
                {actionError}
              </Alert>
            )}

            {/* Approved-domain list */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Approved domains
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {isOn
                  ? "Anyone whose email domain isn't listed here is blocked (admins excepted)."
                  : "These take effect once the toggle above is on."}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  p: 1,
                  minHeight: 44,
                  alignItems: "center",
                  borderRadius: 0.5,
                  bgcolor: "background.default",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {config.domains.length === 0 ? (
                  <Typography variant="caption" color="text.disabled">
                    No domains added yet.
                    {isOn ? " With the toggle on and no domains listed, only admins can log in." : ""}
                  </Typography>
                ) : (
                  config.domains.map((domain) => (
                    <Chip
                      key={domain}
                      label={domain}
                      size="small"
                      onDelete={removingDomain ? undefined : () => void handleRemoveDomain(domain)}
                      deleteIcon={
                        removingDomain === domain ? (
                          <CircularProgress size={14} sx={{ mr: "4px" }} />
                        ) : undefined
                      }
                      disabled={removingDomain !== null && removingDomain !== domain}
                      sx={{ fontWeight: 500 }}
                    />
                  ))
                )}
              </Box>
            </Box>

            {/* Add a domain */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <TextField
                size="small"
                label="Add a domain"
                placeholder="thermofisher.com"
                value={newDomain}
                onChange={(e) => {
                  setNewDomain(e.target.value);
                  if (actionError) setActionError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canAdd) void handleAddDomain();
                }}
                error={newDomain.trim() !== "" && (!newDomainLooksValid || alreadyPresent)}
                helperText={addHelper}
                sx={{ flex: 1, maxWidth: 360 }}
              />
              <Button
                variant="outlined"
                onClick={() => void handleAddDomain()}
                disabled={!canAdd}
                startIcon={
                  addBusy ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <AddOutlinedIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{ mt: 0.25, flexShrink: 0 }}
              >
                Add
              </Button>
            </Box>
          </Stack>
        ) : null}
      </CardContent>

      <Snackbar
        open={snack !== null}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ width: "100%" }}
        >
          {snack}
        </Alert>
      </Snackbar>
    </Card>
  );
}

// ── Password & Data Tab ───────────────────────────────────────

function PasswordDataTab() {
  const [searchEmail, setSearchEmail] = useState("");
  // `found` stores a minimal shape — email + display name — after a lookup.
  const [found, setFound] = useState<{ email: string; displayName: string } | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  function handleSearch() {
    const q = searchEmail.trim().toLowerCase();
    if (!q) return;
    // Placeholder: real implementation will call an API endpoint.
    // For now, display not-found so the UI state is exercised.
    setFound(null);
    setNotFound(true);
    setNewPassword("");
  }

  function handleResetPassword() {
    // Placeholder — wire to POST /api/admin/users/reset-password when ready.
    setNewPassword("");
    setFound(null);
    setSearchEmail("");
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Maintenance mode — top of the tab */}
      <MaintenanceModeCard />

      {/* Tier QoS knobs for the `basic` role (Sam, 2026-05-14) */}
      <BasicUserLimitsCard />

      {/* Beta access control — domain allowlist (Sam, 2026-05-20) */}
      <DomainRestrictionCard />

      {/* Find user */}
      <Card>
        <CardContent>
          <Typography variant="h3" gutterBottom>Reset User Password</Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                label="User email"
                placeholder="user@thermofisher.com"
                value={searchEmail}
                onChange={(e) => { setSearchEmail(e.target.value); setNotFound(false); }}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={handleSearch}>
                Find User
              </Button>
            </Box>
            {notFound && (
              <Alert severity="error">No user found with that email address.</Alert>
            )}
            {found && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "background.default",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="body2">
                  Found: <strong>{found.displayName}</strong> ({found.email})
                </Typography>
                <TextField
                  size="small"
                  label="New password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  disabled={!newPassword}
                  onClick={handleResetPassword}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Reset Password
                </Button>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card sx={{ border: "1px solid", borderColor: "error.main" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 20 }} />
            <Typography variant="h3" color="error.main">Danger Zone</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                bgcolor: "background.default",
                borderRadius: 0.5,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="subtitle2">Reset All User Data</Typography>
                <Typography variant="caption" color="text.secondary">
                  Deletes all scripts, images, and execution history for all users. Cannot be undone.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{ flexShrink: 0, ml: 2 }}
              >
                Reset Data
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                bgcolor: "background.default",
                borderRadius: 0.5,
                border: "1px solid",
                borderColor: "divider",
                mt: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle2">Deploy Fresh Database</Typography>
                <Typography variant="caption" color="text.secondary">
                  Drops all tables and recreates the schema. All data will be permanently deleted.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{ flexShrink: 0, ml: 2 }}
              >
                Fresh Deploy
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

// ── System Library Tab ────────────────────────────────────────

const CATEGORY_SUGGESTIONS = ["Filtering", "Analysis", "Annotation", "Utility"];

// ─ System Images sub-section ──────────────────────────────────

function SystemImagesSection() {
  const [images, setImages] = useState<UserImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadType, setUploadType] = useState<"SEM" | "TEM" | "SDB" | "OPTICAL">("SEM");
  const [uploadTags, setUploadTags] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit dialog
  const [editTarget, setEditTarget] = useState<UserImage | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editType, setEditType] = useState<"SEM" | "TEM" | "SDB" | "OPTICAL">("SEM");
  const [editTags, setEditTags] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<UserImage | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  function loadImages() {
    setLoading(true);
    setError(null);
    adminSystemImagesApi
      .list()
      .then(setImages)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadImages(); }, []);

  function resetUpload() {
    setUploadFile(null);
    setUploadName("");
    setUploadDesc("");
    setUploadType("SEM");
    setUploadTags("");
    setUploadError(null);
  }

  async function handleUploadSubmit() {
    if (!uploadFile || !uploadName.trim()) return;
    setUploadBusy(true);
    setUploadError(null);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("name", uploadName.trim());
      fd.append("description", uploadDesc.trim());
      fd.append("image_type", uploadType);
      if (uploadTags.trim()) fd.append("tags", uploadTags.trim());
      await adminSystemImagesApi.upload(fd);
      setUploadOpen(false);
      resetUpload();
      loadImages();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploadBusy(false);
    }
  }

  function openEdit(img: UserImage) {
    setThumbnailUrl(null);
    setEditTarget(img);
    setEditName(img.name);
    setEditDesc(img.description ?? "");
    setEditType(img.image_type);
    setEditTags(img.tags ?? "");
    setEditError(null);
    imageApi.getThumbnailUrl(img.id).then((url) => setThumbnailUrl(url)).catch(() => setThumbnailUrl(null));
  }

  async function handleEditSubmit() {
    if (!editTarget) return;
    setEditBusy(true);
    setEditError(null);
    try {
      await adminSystemImagesApi.update(editTarget.id, {
        name: editName.trim(),
        description: editDesc.trim(),
        image_type: editType,
        tags: editTags.trim() || undefined,
      });
      setEditTarget(null);
      setThumbnailUrl(null);
      loadImages();
    } catch (e) {
      setEditError(e instanceof Error ? e.message : "Update failed");
    } finally {
      setEditBusy(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await adminSystemImagesApi.remove(deleteTarget.id);
      setDeleteTarget(null);
      loadImages();
    } catch {
      setDeleteTarget(null);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography variant="subtitle2">System Images</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={() => { resetUpload(); setUploadOpen(true); }}
        >
          Upload Image
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {!loading && !error && (
        <Box
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 0.5,
            overflow: "auto",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.default",
              px: 1,
            }}
          >
            {(["Name", "Type", "Tags", "Created", "Actions"] as const).map((h) => (
              <Box
                key={h}
                sx={{
                  flex: h === "Name" ? 2 : h === "Tags" ? 2 : h === "Actions" ? "0 0 100px" : 1,
                  py: 0.75,
                  px: 0.75,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "text.secondary",
                }}
              >
                {h}
              </Box>
            ))}
          </Box>

          {images.length === 0 ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">No system images yet.</Typography>
            </Box>
          ) : (
            images.map((img, i) => (
              <Box
                key={img.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 1,
                  borderBottom: i < images.length - 1 ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box sx={{ flex: 2, py: 0.75, px: 0.75 }}>
                  <Typography variant="caption" noWrap>{img.name}</Typography>
                </Box>
                <Box sx={{ flex: 1, py: 0.75, px: 0.75 }}>
                  <Chip label={img.image_type} size="small" sx={{ height: 18, fontSize: "0.625rem" }} />
                </Box>
                <Box sx={{ flex: 2, py: 0.75, px: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" noWrap>{img.tags ?? "—"}</Typography>
                </Box>
                <Box sx={{ flex: 1, py: 0.75, px: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(img.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ flex: "0 0 100px", py: 0.75, px: 0.75, display: "flex", gap: 0.5 }}>
                  <Button size="small" onClick={() => openEdit(img)} sx={{ minWidth: 0, px: 0.75, fontSize: "0.6875rem" }}>
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => setDeleteTarget(img)} sx={{ minWidth: 0, px: 0.75, fontSize: "0.6875rem" }}>
                    Delete
                  </Button>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onClose={() => { setUploadOpen(false); resetUpload(); }} maxWidth="xs" fullWidth>
        <DialogTitle>Upload System Image</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: "8px !important" }}>
          {uploadError && <Alert severity="error">{uploadError}</Alert>}
          <Button
            variant="outlined"
            size="small"
            component="label"
            sx={{ alignSelf: "flex-start" }}
          >
            {uploadFile ? uploadFile.name : "Choose file…"}
            <input
              ref={fileInputRef}
              type="file"
              accept=".tif,.tiff,.jpg,.jpeg,.png"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setUploadFile(f); if (!uploadName) setUploadName(f.name.replace(/\.[^.]+$/, "")); }
              }}
            />
          </Button>
          <TextField size="small" label="Name" value={uploadName} onChange={(e) => setUploadName(e.target.value)} fullWidth required />
          <TextField size="small" label="Description" value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} fullWidth multiline rows={2} />
          <TextField
            size="small"
            label="Image Type"
            value={uploadType}
            onChange={(e) => setUploadType(e.target.value as "SEM" | "TEM" | "SDB" | "OPTICAL")}
            select
            SelectProps={{ native: true }}
            fullWidth
          >
            {["SEM", "TEM", "SDB", "OPTICAL"].map((t) => <option key={t} value={t}>{t}</option>)}
          </TextField>
          <TextField size="small" label="Tags (comma-separated)" value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadOpen(false); resetUpload(); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={uploadBusy || !uploadFile || !uploadName.trim()}
            onClick={handleUploadSubmit}
            startIcon={uploadBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onClose={() => { setEditTarget(null); setThumbnailUrl(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Edit System Image</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: "8px !important" }}>
          {thumbnailUrl && (
            <Box
              component="img"
              src={thumbnailUrl}
              alt={editName}
              sx={{
                width: "100%",
                maxHeight: 180,
                objectFit: "contain",
                bgcolor: "action.hover",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            />
          )}
          {editError && <Alert severity="error">{editError}</Alert>}
          <TextField size="small" label="Name" value={editName} onChange={(e) => setEditName(e.target.value)} fullWidth required />
          <TextField size="small" label="Description" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} fullWidth multiline rows={2} />
          <TextField
            size="small"
            label="Image Type"
            value={editType}
            onChange={(e) => setEditType(e.target.value as "SEM" | "TEM" | "SDB" | "OPTICAL")}
            select
            SelectProps={{ native: true }}
            fullWidth
          >
            {["SEM", "TEM", "SDB", "OPTICAL"].map((t) => <option key={t} value={t}>{t}</option>)}
          </TextField>
          <TextField size="small" label="Tags (comma-separated)" value={editTags} onChange={(e) => setEditTags(e.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditTarget(null); setThumbnailUrl(null); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={editBusy || !editName.trim()}
            onClick={handleEditSubmit}
            startIcon={editBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete System Image?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteTarget?.name}" will be permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteBusy}
            onClick={handleDeleteConfirm}
            startIcon={deleteBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─ System Scripts sub-section ─────────────────────────────────

interface ScriptDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; code: string; description: string; category: string; tags: string[] }) => Promise<void>;
  initial?: LibraryScript | null;
  colorMode: "light" | "dark";
}

function ScriptDialog({ open, onClose, onSubmit, initial, colorMode }: ScriptDialogProps) {
  const [title, setTitle] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate fields when dialog opens / initial changes
  useEffect(() => {
    if (open) {
      setTitle(initial?.title ?? "");
      setCode(initial?.code ?? "");
      setDescription(initial?.description ?? "");
      setCategory(initial?.category ?? "");
      setTags(initial?.tags?.join(", ") ?? "");
      setError(null);
      setBusy(false);
    }
  }, [open, initial]);

  async function handleSubmit() {
    if (!title.trim() || !code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        title: title.trim(),
        code: code.trim(),
        description: description.trim(),
        category: category.trim(),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initial ? "Edit System Script" : "New System Script"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: "8px !important" }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField size="small" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ flex: 2 }} required />
          <TextField
            size="small"
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{ flex: 1 }}
            inputProps={{ list: "category-suggestions" }}
          />
          <datalist id="category-suggestions">
            {CATEGORY_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
          </datalist>
        </Box>
        <TextField size="small" label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} />
        <TextField size="small" label="Tags (comma-separated)" value={tags} onChange={(e) => setTags(e.target.value)} fullWidth />
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Script Code
          </Typography>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 0.5,
              overflow: "hidden",
              height: 300,
            }}
          >
            <Editor
              height="300px"
              language="python"
              theme={colorMode === "dark" ? "vs-dark" : "light"}
              value={code}
              onChange={(v) => setCode(v ?? "")}
              options={{
                fontSize: 13,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                wordWrap: "on",
                automaticLayout: true,
              }}
            />
          </Box>
        </Box>
        {initial && (
          <Typography variant="caption" color="text.disabled">
            Last updated: {new Date(initial.updated_at).toLocaleString()}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || !title.trim() || !code.trim()}
          onClick={handleSubmit}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {initial ? "Save Changes" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SystemScriptsSection({ colorMode }: { colorMode: "light" | "dark" }) {
  const [scripts, setScripts] = useState<LibraryScript[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<LibraryScript | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LibraryScript | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  function loadScripts() {
    setLoading(true);
    setError(null);
    adminSystemScriptsApi
      .list()
      .then(setScripts)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadScripts(); }, []);

  async function handleCreate(data: { title: string; code: string; description: string; category: string; tags: string[] }) {
    await adminSystemScriptsApi.create({
      title: data.title,
      code: data.code,
      ...(data.description && { description: data.description }),
      ...(data.category && { category: data.category }),
      ...(data.tags.length > 0 && { tags: data.tags }),
    });
    loadScripts();
  }

  async function handleEdit(data: { title: string; code: string; description: string; category: string; tags: string[] }) {
    if (!editTarget) return;
    await adminSystemScriptsApi.update(editTarget.id, {
      title: data.title,
      code: data.code,
      description: data.description,
      category: data.category,
      tags: data.tags,
    });
    loadScripts();
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await adminSystemScriptsApi.remove(deleteTarget.id);
      setDeleteTarget(null);
      loadScripts();
    } catch {
      setDeleteTarget(null);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography variant="subtitle2">System Scripts</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={() => setCreateOpen(true)}
        >
          New Script
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {!loading && !error && (
        <Box
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 0.5,
            overflow: "auto",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.default",
              px: 1,
            }}
          >
            {(["Title", "Category", "Tags", "Updated", "Actions"] as const).map((h) => (
              <Box
                key={h}
                sx={{
                  flex: h === "Title" ? 2 : h === "Tags" ? 2 : h === "Actions" ? "0 0 100px" : 1,
                  py: 0.75,
                  px: 0.75,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "text.secondary",
                }}
              >
                {h}
              </Box>
            ))}
          </Box>

          {scripts.length === 0 ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">No system scripts yet.</Typography>
            </Box>
          ) : (
            scripts.map((script, i) => (
              <Box
                key={script.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 1,
                  borderBottom: i < scripts.length - 1 ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box sx={{ flex: 2, py: 0.75, px: 0.75 }}>
                  <Typography variant="caption" noWrap>{script.title}</Typography>
                </Box>
                <Box sx={{ flex: 1, py: 0.75, px: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">{script.category ?? "—"}</Typography>
                </Box>
                <Box sx={{ flex: 2, py: 0.75, px: 0.75, display: "flex", flexWrap: "wrap", gap: 0.25 }}>
                  {script.tags && script.tags.length > 0
                    ? script.tags.map((t) => (
                        <Chip key={t} label={t} size="small" sx={{ height: 16, fontSize: "0.5625rem" }} />
                      ))
                    : <Typography variant="caption" color="text.disabled">—</Typography>
                  }
                </Box>
                <Box sx={{ flex: 1, py: 0.75, px: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(script.updated_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ flex: "0 0 100px", py: 0.75, px: 0.75, display: "flex", gap: 0.5 }}>
                  <Button size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 12 }} />} onClick={() => setEditTarget(script)} sx={{ minWidth: 0, px: 0.75, fontSize: "0.6875rem" }}>
                    Edit
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteOutlinedIcon sx={{ fontSize: 12 }} />} onClick={() => setDeleteTarget(script)} sx={{ minWidth: 0, px: 0.75, fontSize: "0.6875rem" }}>
                    Del
                  </Button>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}

      {/* Create dialog */}
      <ScriptDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        colorMode={colorMode}
      />

      {/* Edit dialog */}
      <ScriptDialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSubmit={handleEdit}
        initial={editTarget}
        colorMode={colorMode}
      />

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete System Script?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteTarget?.title}" will be permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteBusy}
            onClick={handleDeleteConfirm}
            startIcon={deleteBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SystemLibraryTab({ colorMode }: { colorMode: "light" | "dark" }) {
  const [section, setSection] = useState(0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Tabs
        value={section}
        onChange={(_, v) => setSection(v)}
        sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36 }, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Tab label="System Images" />
        <Tab label="System Scripts" />
      </Tabs>

      {section === 0 && <SystemImagesSection />}
      {section === 1 && <SystemScriptsSection colorMode={colorMode} />}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────

export function AdminPage() {
  const [colorMode] = useColorMode();
  const theme = useMemo(() => createDashboardTheme(colorMode), [colorMode]);
  // If the URL hash is "#maintenance" (set by the maintenance banner's
  // "Disable" button), open the Password & Data tab where the
  // Maintenance Mode card lives. Otherwise default to Users.
  const [activeTab, setActiveTab] = useState<number>(() => {
    if (typeof window !== "undefined" && window.location.hash === "#maintenance") {
      return 1;
    }
    return 0;
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <MapsLogo size="medium" />
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
              Discover Studio
            </Typography>
            <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
              Admin Panel
            </Typography>
          </Box>
          <Box sx={{ ml: "auto" }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowBackOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => { window.location.href = import.meta.env.BASE_URL; }}
            >
              Back to App
            </Button>
          </Box>
        </Box>

        {/* Tab bar */}
        <Box sx={{ bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider", px: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40 } }}
          >
            <Tab label="Users" />
            <Tab label="Password & Data" />
            <Tab label="System Library" />
            <Tab label="Sessions" />
          </Tabs>
        </Box>

        {/* Content. The Sessions tab gets a wider max width than the
            other admin tabs because its filter row + table benefit from
            extra horizontal space. */}
        <Box
          sx={{
            p: 3,
            maxWidth: activeTab === 3 ? 1400 : 1100,
            mx: "auto",
          }}
        >
          {activeTab === 0 && <UsersTab />}
          {activeTab === 1 && <PasswordDataTab />}
          {activeTab === 2 && <SystemLibraryTab colorMode={colorMode} />}
          {activeTab === 3 && <SessionsTab />}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
